/**
 * Utility functions for positioning components on the canvas
 */

import {
  DEFAULT_COMPONENT_HEIGHT,
  DEFAULT_COMPONENT_WIDTH,
  VERTICAL_PADDING,
} from '../../../../utils/layoutConstants';

/**
 * Calculate a position relative to another component (above or below)
 *
 * @param {Object} initialPosition - The initial position {x, y} to use as fallback
 * @param {Array} existingComponents - Array of components to check against
 * @param {Object} dimensions - Dimensions {width, height} of the new component
 * @param {string} relativeToComponentId - ID of the component to position relative to
 * @param {Function} isOverlapping - Function to check if a position overlaps with existing components
 * @returns {Object|null} A non-overlapping position {x, y} or null if none found
 */
const calculateRelativePlacement = (
  initialPosition,
  existingComponents,
  dimensions,
  relativeToComponentId,
  isOverlapping
) => {
  const componentHeight = dimensions.height || DEFAULT_COMPONENT_HEIGHT;

  const relativeComponent = existingComponents.find(c => c.id === relativeToComponentId);

  if (relativeComponent && relativeComponent.position) {
    const relHeight = relativeComponent.height || DEFAULT_COMPONENT_HEIGHT;

    const positionBelow = {
      x: relativeComponent.position.x,
      y: relativeComponent.position.y + relHeight + VERTICAL_PADDING,
    };
    if (!isOverlapping(positionBelow, relativeToComponentId)) {
      return positionBelow;
    }

    const positionAbove = {
      x: relativeComponent.position.x,
      y: relativeComponent.position.y - componentHeight - VERTICAL_PADDING,
    };
    if (!isOverlapping(positionAbove, relativeToComponentId)) {
      return positionAbove;
    }
  }

  return null;
};
/**
 * Brute force in a spiral pattern to find the the closest non overlap position
 */
const findNonOverlapPositionWithSpiralPattern = (
  componentWidth,
  componentHeight,
  initialPosition,
  isOverlapping
) => {
  const spiralStep = Math.max(componentWidth, componentHeight, VERTICAL_PADDING) + VERTICAL_PADDING;
  let position = { ...initialPosition };
  let radius = spiralStep;
  let angle = 0;
  const angleStep = Math.PI / 8;
  const maxAttempts = 100;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    position = {
      x: initialPosition.x + Math.round(radius * Math.cos(angle)),
      y: initialPosition.y + Math.round(radius * Math.sin(angle)),
    };

    if (!isOverlapping(position)) {
      return position;
    }

    angle += angleStep;
    if (angle >= 2 * Math.PI) {
      angle = 0;
      radius += spiralStep;
    }
  }

  return initialPosition;
};

/**
 * Find a position that doesn't overlap with existing components.
 * Can optionally position relative to another component (e.g., stacking).
 *
 * @param {Object} initialPosition - The initial position {x, y} to try (used as fallback or if no relative positioning)
 * @param {Array} existingComponents - Array of components to check against (should include id, position, width, height)
 * @param {Object} dimensions - Optional dimensions {width, height} of the new component being placed
 * @param {string|null} relativeToComponentId - Optional ID of the component to position relative to
 * @param {boolean} isVerticalStack - Optional strategy for relative positioning, default false
 * @returns {Object} A non-overlapping position {x, y}
 */
export const findNonOverlappingPosition = (
  initialPosition,
  existingComponents,
  dimensions = {},
  relativeToComponentId = null,
  isVerticalStack = false
) => {
  const componentWidth = dimensions.width || DEFAULT_COMPONENT_WIDTH;
  const componentHeight = dimensions.height || DEFAULT_COMPONENT_HEIGHT;
  if (existingComponents?.length === 0) {
    return initialPosition;
  }

  const isOverlapping = (position, ignoreComponentId = null) => {
    return existingComponents.some(component => {
      if (component.id === ignoreComponentId) {
        return false;
      }
      const isComponentValid =
        component?.id &&
        component?.position &&
        typeof component.position.x === 'number' &&
        typeof component.position.y === 'number';

      if (isComponentValid) {
        // Calculate bounds for the new component at the candidate position
        const newLeft = position.x;
        const newRight = position.x + componentWidth;
        const newTop = position.y;
        const newBottom = position.y + componentHeight;

        // Calculate bounds for the existing component
        const existingWidth = component.width || DEFAULT_COMPONENT_WIDTH;
        const existingHeight = component.height || DEFAULT_COMPONENT_HEIGHT;
        const existingLeft = component.position.x;
        const existingRight = component.position.x + existingWidth;
        const existingTop = component.position.y;
        const existingBottom = component.position.y + existingHeight;

        const hasHorizontalGap = newRight <= existingLeft || newLeft >= existingRight;
        const hasVerticalGap = newBottom <= existingTop || newTop >= existingBottom;

        return !(hasHorizontalGap || hasVerticalGap);
      }
      return false;
    });
  };

  if (relativeToComponentId && isVerticalStack) {
    const relativePosition = calculateRelativePlacement(
      initialPosition,
      existingComponents,
      dimensions,
      relativeToComponentId,
      isOverlapping
    );

    if (relativePosition) {
      return relativePosition;
    }
  }

  if (!isOverlapping(initialPosition)) {
    return initialPosition;
  }

  return findNonOverlapPositionWithSpiralPattern(
    componentWidth,
    componentHeight,
    initialPosition,
    isOverlapping
  );
};
